import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Wifi as WifiIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';
import {
  getInstutueApp,
  postInstituteAppData,
  putInstituteAppData,
  deleteInstituteAppData,
} from 'modules/platform-components/api-integration-util';

const getHeaders = () => {
  const user = getUserDetails();
  return { tenantId: user.tenantId, userEmail: user.email };
};

// ─── Provider chip colours ────────────────────────────────────────────────
const providerColour = {
  OPENAI: '#10a37f',
  ANTHROPIC: '#d97706',
  GOOGLE_GEMINI: '#4285f4',
};

// ─── Main Component ───────────────────────────────────────────────────────
const AIConfig = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={1}>
        AI Provider Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure which AI provider and model each feature uses. Changes take effect immediately — no restart required.
      </Typography>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Provider Credentials" />
        <Tab label="Feature Settings" />
        <Tab label="Model Registry" />
      </Tabs>

      {activeTab === 0 && <ProvidersTab />}
      {activeTab === 1 && <FeaturesTab />}
      {activeTab === 2 && <ModelsTab />}
    </Box>
  );
};

// ─── Tab 1 · Provider Credentials ────────────────────────────────────────
const ProvidersTab = () => {
  const [configs, setConfigs] = useState([]);
  const [supported, setSupported] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [form, setForm] = useState({ provider: '', apiKey: '', isActive: true });

  const headers = getHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, supRes] = await Promise.all([
        getInstutueApp('admin/ai-config/providers', {}, headers),
        getInstutueApp('admin/ai-config/providers/supported', {}, headers),
      ]);
      setConfigs(cfgRes?.result || []);
      setSupported(supRes?.result || []);
    } catch (e) {
      toast.error('Failed to load provider configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.provider || !form.apiKey) {
      toast.error('Provider and API key are required');
      return;
    }
    try {
      await postInstituteAppData('admin/ai-config/providers', {
        data: form,
        headers,
      });
      toast.success('Provider config saved');
      setDialogOpen(false);
      setForm({ provider: '', apiKey: '', isActive: true });
      load();
    } catch (e) {
      toast.error(e || 'Failed to save provider config');
    }
  };

  const handleToggleActive = async (provider, current) => {
    try {
      await putInstituteAppData(
        `admin/ai-config/providers/${provider}/active?active=${!current}`,
        { headers }
      );
      toast.success(`Provider ${!current ? 'activated' : 'deactivated'}`);
      load();
    } catch (e) {
      toast.error(e || 'Failed to update provider');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteInstituteAppData(`admin/ai-config/providers/${providerToDelete}`, { headers });
      toast.success('Provider config deleted');
      setDeleteDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e || 'Failed to delete provider config');
    }
  };

  const handleTest = async (provider) => {
    setTesting(prev => ({ ...prev, [provider]: 'loading' }));
    try {
      const res = await postInstituteAppData(`admin/ai-config/providers/${provider}/test`, { headers });
      const ok = res?.result === true;
      setTesting(prev => ({ ...prev, [provider]: ok ? 'ok' : 'fail' }));
      toast[ok ? 'success' : 'error'](ok ? `${provider} connection OK` : `${provider} connection failed`);
    } catch (e) {
      setTesting(prev => ({ ...prev, [provider]: 'fail' }));
      toast.error(`${provider} connection failed`);
    }
  };

  const configuredProviders = new Set(configs.map(c => c.provider));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight={500}>
          API Keys ({configs.length} configured)
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Add Provider
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : configs.length === 0 ? (
        <Alert severity="info">
          No provider credentials configured yet. Add at least one provider to enable AI features.
        </Alert>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {configs.map(cfg => (
            <Box key={cfg.provider} sx={{
              border: '1px solid #e0e0e0', borderRadius: 2, p: 2,
              display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'
            }}>
              <Chip
                label={cfg.provider}
                sx={{ bgcolor: providerColour[cfg.provider] || '#666', color: '#fff', fontWeight: 600 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                API Key: ••••••••••••••••
              </Typography>
              <Chip
                label={cfg.isActive ? 'Active' : 'Inactive'}
                color={cfg.isActive ? 'success' : 'default'}
                size="small"
              />

              {/* Test connection */}
              <Tooltip title="Test connection">
                <span>
                  <IconButton size="small" onClick={() => handleTest(cfg.provider)}>
                    {testing[cfg.provider] === 'loading' ? <CircularProgress size={18} /> :
                     testing[cfg.provider] === 'ok' ? <CheckCircleIcon color="success" fontSize="small" /> :
                     testing[cfg.provider] === 'fail' ? <CancelIcon color="error" fontSize="small" /> :
                     <WifiIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>

              {/* Toggle active */}
              <Tooltip title={cfg.isActive ? 'Deactivate' : 'Activate'}>
                <Switch
                  size="small"
                  checked={!!cfg.isActive}
                  onChange={() => handleToggleActive(cfg.provider, cfg.isActive)}
                />
              </Tooltip>

              {/* Delete */}
              <Tooltip title="Delete credential">
                <IconButton size="small" color="error" onClick={() => {
                  setProviderToDelete(cfg.provider);
                  setDeleteDialogOpen(true);
                }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Provider Credential</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                value={form.provider}
                label="Provider"
                onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
              >
                {supported.map(s => (
                  <MenuItem key={s.key} value={s.key}>
                    {s.displayName}
                    {configuredProviders.has(s.key) && (
                      <Chip label="configured" size="small" sx={{ ml: 1 }} />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="API Key"
              type="password"
              fullWidth
              value={form.apiKey}
              onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))}
              helperText="Stored encrypted at rest. Never shown again after saving."
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Provider Config</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{providerToDelete}</strong> credentials? Any features using this provider will
            fall back to the default OpenAI configuration.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Tab 2 · Feature Settings ─────────────────────────────────────────────
const FeaturesTab = () => {
  const [configs, setConfigs] = useState([]);
  const [supportedFeatures, setSupportedFeatures] = useState([]);
  const [supportedProviders, setSupportedProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form, setForm] = useState({
    featureKey: '', provider: '', modelId: '',
    maxTokens: '', temperature: '',
    fallbackProvider: '', fallbackModelId: '',
  });
  const [testingModel, setTestingModel] = useState(null); // null | 'loading' | 'ok' | 'fail'
  const [testingFallback, setTestingFallback] = useState(null);

  const headers = getHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, featRes, provRes, modRes] = await Promise.all([
        getInstutueApp('admin/ai-config/features', {}, headers),
        getInstutueApp('admin/ai-config/features/supported', {}, headers),
        getInstutueApp('admin/ai-config/providers/supported', {}, headers),
        getInstutueApp('admin/ai-config/models', {}, headers),
      ]);
      setConfigs(cfgRes?.result || []);
      setSupportedFeatures(featRes?.result || []);
      setSupportedProviders(provRes?.result || []);
      setModels(modRes?.result || []);
    } catch (e) {
      toast.error('Failed to load feature configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const chatModelsFor = (providerKey) =>
    models.filter(m => m.provider === providerKey && (m.capability === 'CHAT' || m.capability === 'BOTH'));

  const openAddDialog = () => {
    setEditingConfig(null);
    setForm({ featureKey: '', provider: '', modelId: '', maxTokens: '', temperature: '', fallbackProvider: '', fallbackModelId: '' });
    setTestingModel(null);
    setTestingFallback(null);
    setDialogOpen(true);
  };

  const openEditDialog = (cfg) => {
    setEditingConfig(cfg);
    setForm({
      featureKey: cfg.featureKey,
      provider: cfg.provider,
      modelId: cfg.modelId,
      maxTokens: cfg.maxTokens || '',
      temperature: cfg.temperature || '',
      fallbackProvider: cfg.fallbackProvider || '',
      fallbackModelId: cfg.fallbackModelId || '',
    });
    setTestingModel(null);
    setTestingFallback(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.featureKey || !form.provider || !form.modelId) {
      toast.error('Feature, provider, and model are required');
      return;
    }
    try {
      const payload = {
        featureKey: form.featureKey,
        provider: form.provider,
        modelId: form.modelId,
        maxTokens: form.maxTokens ? parseInt(form.maxTokens) : null,
        temperature: form.temperature !== '' ? parseFloat(form.temperature) : null,
        fallbackProvider: form.fallbackProvider || null,
        fallbackModelId: form.fallbackModelId || null,
        isActive: true,
      };
      await postInstituteAppData('admin/ai-config/features', { data: payload, headers });
      toast.success('Feature config saved');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e || 'Failed to save feature config');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteInstituteAppData(`admin/ai-config/features/${featureToDelete}`, { headers });
      toast.success('Feature config deleted — will revert to default OpenAI');
      setDeleteDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e || 'Failed to delete feature config');
    }
  };

  const handleTestModel = async (provider, model, setStatus) => {
    if (!provider || !model) {
      toast.error('Select a provider and model first');
      return;
    }
    setStatus('loading');
    try {
      const res = await postInstituteAppData(`admin/ai-config/providers/${provider}/test-model?model=${encodeURIComponent(model)}`, { headers });
      const ok = res?.result === true;
      setStatus(ok ? 'ok' : 'fail');
      toast[ok ? 'success' : 'error'](ok ? `${model} accessible` : `${model} not accessible — check model ID and project permissions`);
    } catch (e) {
      setStatus('fail');
      toast.error(`${model} test failed`);
    }
  };

  const configuredKeys = new Set(configs.map(c => c.featureKey));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight={500}>
          Feature Routing ({configs.length} configured, {supportedFeatures.length - configs.length} using default OpenAI)
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
          Configure Feature
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <>
          {/* Configured features */}
          {configs.length > 0 && (
            <Box display="flex" flexDirection="column" gap={2} mb={3}>
              {configs.map(cfg => (
                <Box key={cfg.featureKey} sx={{
                  border: '1px solid #e0e0e0', borderRadius: 2, p: 2,
                  display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'
                }}>
                  <Box flex={1} minWidth={200}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {supportedFeatures.find(f => f.key === cfg.featureKey)?.displayName || cfg.featureKey}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{cfg.featureKey}</Typography>
                  </Box>
                  <Chip
                    label={cfg.provider}
                    size="small"
                    sx={{ bgcolor: providerColour[cfg.provider] || '#666', color: '#fff' }}
                  />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{cfg.modelId}</Typography>
                  {cfg.maxTokens && (
                    <Chip label={`${cfg.maxTokens} tokens`} size="small" variant="outlined" />
                  )}
                  {cfg.temperature != null && cfg.temperature !== '' && (
                    <Chip label={`temp ${cfg.temperature}`} size="small" variant="outlined" />
                  )}
                  {cfg.fallbackProvider && (
                    <Chip
                      label={`fallback: ${cfg.fallbackProvider}`}
                      size="small"
                      variant="outlined"
                      color="warning"
                    />
                  )}
                  <IconButton size="small" onClick={() => openEditDialog(cfg)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => {
                    setFeatureToDelete(cfg.featureKey);
                    setDeleteDialogOpen(true);
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Unconfigured features (using default) */}
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            Using default OpenAI configuration:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {supportedFeatures
              .filter(f => !configuredKeys.has(f.key))
              .map(f => (
                <Chip
                  key={f.key}
                  label={f.displayName}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setForm(prev => ({ ...prev, featureKey: f.key }));
                    openAddDialog();
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
          </Box>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingConfig ? 'Edit Feature Config' : 'Configure Feature'}</DialogTitle>
        <DialogContent>
          {form.featureKey === 'EMBEDDING' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Changing the embedding provider/model will invalidate all existing vector embeddings.
              A full re-indexing job must be triggered after saving.
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Feature</InputLabel>
              <Select
                value={form.featureKey}
                label="Feature"
                onChange={e => setForm(f => ({ ...f, featureKey: e.target.value, modelId: '' }))}
                disabled={!!editingConfig}
              >
                {supportedFeatures.map(f => (
                  <MenuItem key={f.key} value={f.key}>{f.displayName}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                value={form.provider}
                label="Provider"
                onChange={e => setForm(f => ({ ...f, provider: e.target.value, modelId: '' }))}
              >
                {supportedProviders.map(p => (
                  <MenuItem key={p.key} value={p.key}>{p.displayName}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box display="flex" gap={1} alignItems="flex-start">
              <FormControl fullWidth>
                <InputLabel>Model</InputLabel>
                <Select
                  value={form.modelId}
                  label="Model"
                  onChange={e => { setForm(f => ({ ...f, modelId: e.target.value })); setTestingModel(null); }}
                  disabled={!form.provider}
                >
                  {chatModelsFor(form.provider).map(m => (
                    <MenuItem key={m.modelId} value={m.modelId}>
                      <Box>
                        <Typography variant="body2">{m.displayName}</Typography>
                        {m.notes && <Typography variant="caption" color="text.secondary">{m.notes}</Typography>}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Test if this model is accessible">
                <span>
                  <IconButton
                    size="small"
                    sx={{ mt: 1 }}
                    disabled={!form.provider || !form.modelId || testingModel === 'loading'}
                    onClick={() => handleTestModel(form.provider, form.modelId, setTestingModel)}
                  >
                    {testingModel === 'loading' ? <CircularProgress size={18} /> :
                     testingModel === 'ok' ? <CheckCircleIcon color="success" fontSize="small" /> :
                     testingModel === 'fail' ? <CancelIcon color="error" fontSize="small" /> :
                     <WifiIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                label="Max Tokens (optional)"
                type="number"
                value={form.maxTokens}
                onChange={e => setForm(f => ({ ...f, maxTokens: e.target.value }))}
                helperText="Overrides service default"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Temperature (optional)"
                type="number"
                inputProps={{ step: 0.1, min: 0, max: 2 }}
                value={form.temperature}
                onChange={e => setForm(f => ({ ...f, temperature: e.target.value }))}
                helperText="0.0 – 2.0"
                sx={{ flex: 1 }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" mt={1}>Fallback (optional — used if primary fails)</Typography>
            <Box display="flex" gap={2}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Fallback Provider</InputLabel>
                <Select
                  value={form.fallbackProvider}
                  label="Fallback Provider"
                  onChange={e => setForm(f => ({ ...f, fallbackProvider: e.target.value, fallbackModelId: '' }))}
                >
                  <MenuItem value="">None</MenuItem>
                  {supportedProviders.map(p => (
                    <MenuItem key={p.key} value={p.key}>{p.displayName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Fallback Model</InputLabel>
                <Select
                  value={form.fallbackModelId}
                  label="Fallback Model"
                  onChange={e => { setForm(f => ({ ...f, fallbackModelId: e.target.value })); setTestingFallback(null); }}
                  disabled={!form.fallbackProvider}
                >
                  {chatModelsFor(form.fallbackProvider).map(m => (
                    <MenuItem key={m.modelId} value={m.modelId}>{m.displayName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Test fallback model">
                <span>
                  <IconButton
                    size="small"
                    sx={{ mt: 1 }}
                    disabled={!form.fallbackProvider || !form.fallbackModelId || testingFallback === 'loading'}
                    onClick={() => handleTestModel(form.fallbackProvider, form.fallbackModelId, setTestingFallback)}
                  >
                    {testingFallback === 'loading' ? <CircularProgress size={18} /> :
                     testingFallback === 'ok' ? <CheckCircleIcon color="success" fontSize="small" /> :
                     testingFallback === 'fail' ? <CancelIcon color="error" fontSize="small" /> :
                     <WifiIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Reset Feature to Default</DialogTitle>
        <DialogContent>
          <Typography>
            Remove the config for <strong>{featureToDelete}</strong>? This feature will revert to the default OpenAI behaviour.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Reset to Default</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Tab 3 · Model Registry ───────────────────────────────────────────────
const ModelsTab = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providerFilter, setProviderFilter] = useState('');
  const [capabilityFilter, setCapabilityFilter] = useState('');

  const headers = getHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (providerFilter) params.provider = providerFilter;
      if (capabilityFilter) params.capability = capabilityFilter;
      const res = await getInstutueApp('admin/ai-config/models', params, headers);
      setModels(res?.result || []);
    } catch (e) {
      toast.error('Failed to load model registry');
    } finally {
      setLoading(false);
    }
  }, [providerFilter, capabilityFilter]);

  useEffect(() => { load(); }, [load]);

  const grouped = models.reduce((acc, m) => {
    acc[m.provider] = acc[m.provider] || [];
    acc[m.provider].push(m);
    return acc;
  }, {});

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={500} flex={1}>
          Available Models ({models.length})
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Provider</InputLabel>
          <Select value={providerFilter} label="Provider" onChange={e => setProviderFilter(e.target.value)}>
            <MenuItem value="">All providers</MenuItem>
            <MenuItem value="OPENAI">OpenAI</MenuItem>
            <MenuItem value="ANTHROPIC">Anthropic Claude</MenuItem>
            <MenuItem value="GOOGLE_GEMINI">Google Gemini</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Capability</InputLabel>
          <Select value={capabilityFilter} label="Capability" onChange={e => setCapabilityFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="CHAT">Chat</MenuItem>
            <MenuItem value="EMBEDDING">Embedding</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        Object.entries(grouped).map(([provider, provModels]) => (
          <Box key={provider} mb={4}>
            <Typography variant="h6" sx={{
              mb: 1, pb: 1, borderBottom: '2px solid',
              borderColor: providerColour[provider] || '#999',
              color: providerColour[provider] || 'inherit',
            }}>
              {provider.replace('_', ' ')}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {provModels.map(m => (
                <Box key={m.modelId} sx={{
                  border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5,
                  display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'
                }}>
                  <Typography variant="body2" fontFamily="monospace" fontWeight={600} flex={1} minWidth={200}>
                    {m.modelId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" flex={2}>
                    {m.displayName}
                  </Typography>
                  <Chip
                    label={m.capability}
                    size="small"
                    color={m.capability === 'EMBEDDING' ? 'secondary' : 'primary'}
                    variant="outlined"
                  />
                  {m.contextWindow && (
                    <Chip label={`${(m.contextWindow / 1000).toFixed(0)}k ctx`} size="small" variant="outlined" />
                  )}
                  {m.notes && (
                    <Tooltip title={m.notes}>
                      <Typography variant="caption" color="text.secondary" sx={{
                        maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {m.notes}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
};

export default AIConfig;
